import type { ReactNode } from 'react'

import { Volume2, VolumeX } from 'lucide-react'
import { cn } from '@/lib/utils'

import {
    setMotion,
    setSound,
    toggleLight,
    usePreferences
} from '@/lib/preferences'

interface ToggleProps {
    className?: string
    showLabel?: boolean
}

/** Switches the whole site to deep red on black, the light astronomers use to keep their night vision. */
export const RedLightToggle = ({ className, showLabel = true }: ToggleProps): ReactNode => {
    const { light } = usePreferences()
    const active = light === 'red'

    return (
        <button
            type='button'
            onClick={toggleLight}
            aria-pressed={active}
            aria-label={active ? 'Turn red-light mode off' : 'Turn red-light mode on'}
            className={cn(
                'group inline-flex h-9 items-center gap-2.5 rounded-full px-3 text-star-2 transition-colors duration-500 hover:text-star',
                className
            )}
        >
            <span className='relative flex size-3.5 items-center justify-center'>
                <span
                    className={cn(
                        'absolute inset-0 rounded-full border transition-all duration-700',
                        active ? 'scale-100 border-signal' : 'scale-75 border-hair-2 group-hover:scale-100'
                    )}
                />
                <span className={cn('size-1.5 rounded-full bg-signal transition-transform duration-700', active && 'scale-125')} />
            </span>
            {showLabel && (
                <span className='label-mono'>
                    {active ? 'Red light on' : 'Red light'}
                </span>
            )}
        </button>
    )
}

export const SoundToggle = ({ className }: ToggleProps): ReactNode => {
    const { sound } = usePreferences()

    return (
        <button
            type='button'
            onClick={() => setSound(!sound)}
            aria-pressed={sound}
            className={cn(
                'inline-flex h-9 items-center gap-2 rounded-full text-star-3 transition-colors duration-500 hover:text-star',
                className
            )}
        >
            {sound ? <Volume2 aria-hidden className='size-4' strokeWidth={1.25} /> : <VolumeX aria-hidden className='size-4' strokeWidth={1.25} />}
            <span className='label-mono'>
                {sound ? 'Sound: on' : 'Sound: off'}
            </span>
        </button>
    )
}

export const MotionToggle = ({ className }: ToggleProps): ReactNode => {
    const { motion } = usePreferences()
    const reduced = motion === 'reduced'

    return (
        <button
            type='button'
            role='switch'
            aria-checked={reduced}
            onClick={() => setMotion(reduced ? 'full' : 'reduced')}
            className={cn('group inline-flex items-center gap-3 text-star-3 transition-colors duration-500 hover:text-star', className)}
        >
            <span className='label-mono'>
                Reduce motion
            </span>
            <span className={cn('relative h-4 w-7 rounded-full border transition-colors duration-500', reduced ? 'border-brass' : 'border-hair-2')}>
                <span
                    className={cn(
                        'absolute top-1/2 size-2 -translate-y-1/2 rounded-full transition-all duration-500',
                        reduced ? 'left-[0.9rem] bg-brass' : 'left-[0.2rem] bg-star-3'
                    )}
                />
            </span>
        </button>
    )
}
